<script setup lang="ts">
type CustomTestData = App.Configuration.Data.CustomTestData;
type EnumData = App.Base.Data.EnumData;
type TestData = App.Students.Data.TestData;
type DataRequestData = App.DataRequests.Data.DataRequestData;
type PartialTestData = App.Students.Data.PartialTestData;

import { buildSelectableCustomTest } from '@/utils/buildSelectable';

import DataRequestSlideout from '@/domains/data-request/components/adviser/DataRequestSlideout.vue';
import DataRequestSlideoutConfirmationDialog from '@/domains/data-request/components/adviser/DataRequestSlideoutConfirmationDialog.vue';
import TestFormFields from '@/components/students/studentActions/TestFormFields.vue';

export type ShowDataRequestProps = {
  apCourses: EnumData[];
  customTests: CustomTestData[];
  dataRequest: DataRequestData;
  labelMap: Record<string, string>;
  scoreMap: Record<string, Record<string, { rules?: string[]; step?: number }>>;
  test: TestData | null;
  types: EnumData[];
};

const props = defineProps<ShowDataRequestProps>();

const [isRejectDialogOpen, rejectDialogToggle] = useToggle(false);

const dataRequestAttributes = computed(() => props.dataRequest.attributes as PartialTestData);

const isPending = computed(() => props.dataRequest.status.value === 'pending');

const typeOptions = computed(() => [
  ...props.types,
  {
    value: 'custom-test',
    label: 'Custom test',
    description: null
  }
]);

const pickedType = computed(
  () => dataRequestAttributes.value?.type?.value ?? props.test?.type?.value ?? null
);

const isCustomTest = computed(() => {
  return !props.types.find((type) => type.value === pickedType.value);
});

const form = useForm({
  fields: {
    type:
      typeOptions.value.find((opt) => opt.value === pickedType.value) ??
      typeOptions.value.find((opt) => opt.value === 'custom-test'),
    subject: getSubject(),
    test: getTest(),
    date: (dataRequestAttributes.value?.date || props.test?.date) ?? '',
    is_complete: dataRequestAttributes.value?.is_complete ?? props.test?.is_complete ?? false,
    scores:
      (dataRequestAttributes.value?.scores || props.test?.scores)?.reduce(
        (carry: Record<string, number>, score: App.Students.Data.TestScoreData) => ({
          ...carry,
          [score.name]: score.score
        }),
        {}
      ) ?? {},
    is_assisted: dataRequestAttributes.value?.is_assisted ?? props.test?.is_assisted ?? false,
    is_waiver_assisted:
      dataRequestAttributes.value?.is_waiver_assisted ?? props.test?.is_waiver_assisted ?? false,
    is_registered: dataRequestAttributes.value?.is_registered ?? props.test?.is_registered ?? false,
    is_waived: dataRequestAttributes.value?.is_waived ?? props.test?.is_waived ?? false,
    ...(isCustomTest.value
      ? (dataRequestAttributes.value?.scores || props.test?.scores)?.reduce(
          (carry: Record<string, number>, score: App.Students.Data.TestScoreData) => ({
            ...carry,
            [score.name]: score.score
          }),
          {}
        )
      : {})
  },
  transform: (fields) => ({
    ...fields,
    data_request_id: props.dataRequest.id,
    return_to_route: 'data-requests.index',
    type: fields.type?.value === 'custom-test' ? fields.test?.value : fields.type?.value,
    test: fields.test?.value
  })
});

const rejectForm = useForm({
  method: 'POST',
  url: route('data-requests.reject', {
    dataRequest: props.dataRequest.id
  }),
  fields: {
    rejected_reason: ''
  },
  hooks: {
    success() {
      rejectDialogToggle();
    }
  }
});

function getSubject() {
  if (dataRequestAttributes.value?.type?.value === 'ap-course') {
    const subjectScore = dataRequestAttributes.value?.scores?.find(
      (score) => score.name === 'subject'
    )?.score;
    return props.apCourses.find((subject) => subject.value === subjectScore);
  }
  return null;
}

function getTest() {
  return !props.types.find((type) => type.value === pickedType.value)
    ? buildSelectableCustomTest(props.customTests.find((test) => test.name === pickedType.value))
    : null;
}

function handleApproveDataRequest() {
  if (props.dataRequest.type.value === 'create') {
    form.submitWith({
      method: 'POST',
      url: route('students.tests.store', {
        student: props.dataRequest.user?.id
      })
    });
  }
  if (props.dataRequest.type.value === 'edit') {
    form.submitWith({
      method: 'PUT',
      url: route('students.tests.update', {
        test: props.dataRequest.requestable?.id,
        student: props.dataRequest.user?.id
      })
    });
  }
}

function handleRejectDataRequest() {
  rejectDialogToggle();
}

function handleRejectDataRequestConfirm() {
  rejectForm.submit();
}
</script>

<template>
  <DataRequestSlideout
    :dataRequest="dataRequest"
    :hasErrors="form.hasErrors"
    :isApproveLoading="form.processing"
    :isDirty="form.isDirty"
    @approveDataRequest="handleApproveDataRequest"
    @rejectDataRequest="handleRejectDataRequest"
  >
    <TestFormFields
      :apCourses="apCourses"
      :customTests="customTests"
      :form="form"
      :isReadonly="!isPending"
      :labelMap="labelMap"
      :scoreMap="scoreMap"
      :test="test || undefined"
      :types="typeOptions"
    />
  </DataRequestSlideout>
  <DataRequestSlideoutConfirmationDialog
    :form="rejectForm"
    :isOpen="isRejectDialogOpen"
    @onConfirm="() => handleRejectDataRequestConfirm()"
    @onCancel="() => rejectDialogToggle()"
    @onClose="() => rejectDialogToggle()"
  />
</template>
